import {
  ADD_CREDIT_LIMIT_ALLOCATION,
  ADD_CREDIT_LIMITS,
  ADD_PARTNERS,
  BUYER,
  CL_APPROVED,
  CL_REQUESTED,
  REGISTER_FOR_FINANCING,
  SELLER,
  SUPPLIER,
} from "codes";
import { areBuyerFinancialStatementsComplete } from "./buyerFinancialDocuments";
import { getPresalesRouteForStep } from "./presalesNavigation";
import { isReverseFactoringBuyerAllocationComplete } from "components/Company/Lead/utils/reverseFactoringFlow";
import type { Account, CreditLimit, PartnerData } from "types";

const SUBMITTED_CL_STATUSES = [CL_REQUESTED, CL_APPROVED] as const;

export const isPartnerCreditLimitSubmitted = (
  creditLimit?: Pick<CreditLimit, "id" | "status"> | null,
) => {
  const status = String(creditLimit?.status ?? "").toUpperCase();
  return (
    Boolean(creditLimit?.id) &&
    SUBMITTED_CL_STATUSES.includes(
      status as (typeof SUBMITTED_CL_STATUSES)[number],
    )
  );
};

const isSupplierPartnerCreditLimitSubmitted = (
  partner: Pick<PartnerData, "credit_limit" | "approval">,
) => {
  if (isPartnerCreditLimitSubmitted(partner.credit_limit)) {
    return true;
  }
  const approval = String(partner.approval ?? "").toUpperCase();
  return SUBMITTED_CL_STATUSES.includes(
    approval as (typeof SUBMITTED_CL_STATUSES)[number],
  );
};

/** True when at least one supplier partner has a submitted CL (legacy helper). */
export const hasSubmittedSupplierPartnerCreditLimits = (
  partners?: Array<Pick<PartnerData, "credit_limit" | "approval">> | null,
) =>
  Boolean(
    partners?.some((partner) => isSupplierPartnerCreditLimitSubmitted(partner)),
  );

/**
 * Supplier factoring presales completion: every persisted partner must have
 * CL_REQUESTED or CL_APPROVED (not just the first partner).
 */
export const areAllSupplierPartnerCreditLimitsSubmitted = (
  partners?: Array<Pick<PartnerData, "credit_limit" | "approval">> | null,
) => {
  if (!partners?.length) {
    return false;
  }
  return partners.every((partner) =>
    isSupplierPartnerCreditLimitSubmitted(partner),
  );
};

type PresalesProgressInput = {
  account?: Pick<
    Account,
    "credit_limit" | "id" | "missing_values_before_sf_export" | "profile"
  > | null;
  partners?: Array<
    Pick<PartnerData, "id" | "credit_limit" | "country_matrix_blocked">
  > | null;
  /** Fallback when account.profile is not yet on GET /accounts (e.g. right after profile POST). */
  companyType?: string | null;
  /** Fallback when account.credit_limit is stale but steps form has CL_REQUESTED. */
  clStatus?: string | null;
  /** Account documents — buyer RF must finish financial uploads before step 2+. */
  uploadedDocuments?: Array<{
    document_type?: string;
    document_type_name?: string;
  }> | null;
  /** Partners saved in update-steps when API partners are not yet persisted. */
  savedPartnerList?: unknown[] | null;
};

type PresalesProgress = {
  companyType: string;
  route: string;
  step: number;
};

export type PresalesResumeInput = PresalesProgressInput & {
  /** Saved step from GET /steps or redux (may be stale after re-login). */
  persistedStep?: number;
};

export type PresalesResume = PresalesProgress & {
  /** True when profile is already stored on the account. */
  hasProfile: boolean;
  /** Step index used by the stepper (never rewinds below account progress). */
  activeStep: number;
  /** Route the user should land on after login or refresh. */
  activeRoute: string;
};

const normalizeProfile = (profile?: string | null) =>
  (profile ?? "").trim().toLowerCase();

const profileFromCompanyType = (companyType?: string | null) => {
  const normalized = normalizeProfile(companyType);
  if (normalized === BUYER.toLowerCase()) {
    return BUYER.toLowerCase();
  }
  if (
    normalized === SELLER ||
    normalized === SUPPLIER.toLowerCase() ||
    normalized === "supplier"
  ) {
    return SELLER;
  }
  return "";
};

export const hasPersistedAccountProfile = (
  account?: PresalesProgressInput["account"] | null,
  companyType?: string | null,
) =>
  Boolean(
    normalizeProfile(account?.profile) || profileFromCompanyType(companyType),
  );

const getPersistedPartnerCount = (
  account?: Pick<Account, "missing_values_before_sf_export"> | null,
) =>
  Object.keys(account?.missing_values_before_sf_export?.partners ?? {}).length;

const hasPersistedPartners = (
  partners?: PresalesProgressInput["partners"],
  account?: PresalesProgressInput["account"],
) => Boolean(partners?.length) || getPersistedPartnerCount(account) > 0;

const hasDraftPartnerList = (savedPartnerList?: unknown[] | null) =>
  Boolean(savedPartnerList?.length);

const hasSubmittedBuyerCreditLimit = (
  creditLimit?: Pick<Account["credit_limit"], "id" | "status"> | null,
) => isPartnerCreditLimitSubmitted(creditLimit);

export const getPresalesProgress = ({
  account,
  partners,
  companyType,
  clStatus,
  uploadedDocuments,
  savedPartnerList,
  persistedStep = 0,
}: PresalesProgressInput & { persistedStep?: number }): PresalesProgress => {
  const profile =
    normalizeProfile(account?.profile) || profileFromCompanyType(companyType);
  const persistedPartners = hasPersistedPartners(partners, account);
  const draftPartnerList = hasDraftPartnerList(savedPartnerList);
  const hasPartners =
    persistedPartners || (draftPartnerList && persistedStep >= 3);

  if (!account?.id) {
    return {
      companyType: "",
      route: REGISTER_FOR_FINANCING,
      step: 0,
    };
  }

  if (profile === BUYER.toLowerCase()) {
    const normalizedClStatus = String(clStatus ?? "").toUpperCase();
    const buyerCreditLimitSubmitted =
      hasSubmittedBuyerCreditLimit(account.credit_limit) ||
      SUBMITTED_CL_STATUSES.includes(
        normalizedClStatus as (typeof SUBMITTED_CL_STATUSES)[number],
      );

    if (!buyerCreditLimitSubmitted) {
      return {
        companyType: BUYER,
        route: ADD_CREDIT_LIMITS,
        step: 1,
      };
    }

    const financialDocsComplete =
      areBuyerFinancialStatementsComplete(uploadedDocuments);
    if (!financialDocsComplete) {
      return {
        companyType: BUYER,
        route: ADD_CREDIT_LIMITS,
        step: 1,
      };
    }

    // Draft partners saved while stepping back must not skip to allocation.
    if (persistedStep <= 1 && !persistedPartners && draftPartnerList) {
      return {
        companyType: BUYER,
        route: ADD_CREDIT_LIMITS,
        step: 1,
      };
    }

    if (!hasPartners) {
      return {
        companyType: BUYER,
        route: ADD_PARTNERS,
        step: 2,
      };
    }

    if (
      isReverseFactoringBuyerAllocationComplete({
        buyerCreditLimit: account.credit_limit,
        partners,
      })
    ) {
      return {
        companyType: BUYER,
        route: ADD_CREDIT_LIMIT_ALLOCATION,
        step: 4,
      };
    }

    return {
      companyType: BUYER,
      route: ADD_CREDIT_LIMIT_ALLOCATION,
      step: 3,
    };
  }

  if (profile === SELLER || profile === SUPPLIER.toLowerCase()) {
    const normalizedClStatus = String(clStatus ?? "").toUpperCase();
    const supplierCreditLimitSubmitted =
      SUBMITTED_CL_STATUSES.includes(
        normalizedClStatus as (typeof SUBMITTED_CL_STATUSES)[number],
      ) || areAllSupplierPartnerCreditLimitsSubmitted(partners);

    if (supplierCreditLimitSubmitted) {
      return {
        companyType: SUPPLIER,
        route: ADD_CREDIT_LIMITS,
        step: 3,
      };
    }

    if (persistedPartners) {
      return {
        companyType: SUPPLIER,
        route: ADD_CREDIT_LIMITS,
        step: 2,
      };
    }

    // Draft partner rows saved via update-steps only count after the user clicked Next (step >= 2).
    if (draftPartnerList && persistedStep >= 2) {
      return {
        companyType: SUPPLIER,
        route: ADD_CREDIT_LIMITS,
        step: 2,
      };
    }

    if (persistedStep >= 1) {
      return {
        companyType: SUPPLIER,
        route: ADD_PARTNERS,
        step: 1,
      };
    }

    return {
      companyType: SUPPLIER,
      route: REGISTER_FOR_FINANCING,
      step: 0,
    };
  }

  return {
    companyType: "",
    route: REGISTER_FOR_FINANCING,
    step: 0,
  };
};

/**
 * Single source of truth for presales resume after login, refresh, or menu navigation.
 * Account API (profile, CL, partners) always wins over stale update-steps payloads.
 */
export const resolvePresalesResume = ({
  persistedStep = 0,
  ...progressInput
}: PresalesResumeInput): PresalesResume => {
  const progress = getPresalesProgress({
    ...progressInput,
    persistedStep,
  });
  const hasProfile = hasPersistedAccountProfile(
    progressInput.account,
    progressInput.companyType ?? progress.companyType,
  );

  if (!progressInput.account?.id) {
    return {
      ...progress,
      hasProfile,
      activeStep: persistedStep,
      activeRoute: progress.route,
    };
  }

  const isBuyerFlow =
    progress.companyType === BUYER ||
    progress.companyType.toLowerCase() === BUYER.toLowerCase();
  const onboardingComplete = isBuyerFlow
    ? progress.step >= 4
    : progress.step >= 3;
  const honoursExplicitBack =
    hasProfile &&
    !onboardingComplete &&
    persistedStep > 0 &&
    persistedStep < progress.step;

  // Account-derived step wins on re-login (stale 0) and after completion.
  // An explicit in-progress Back (persistedStep < progress.step) must not skip
  // partners/CL details by jumping forward again.
  const activeStep = honoursExplicitBack
    ? persistedStep
    : hasProfile
      ? Math.max(
          progress.step,
          persistedStep >= 4
            ? persistedStep
            : Math.min(persistedStep, progress.step),
        )
      : persistedStep;

  const activeRoute = honoursExplicitBack
    ? getPresalesRouteForStep(progress.companyType, persistedStep)
    : progress.route;

  return {
    ...progress,
    hasProfile,
    activeStep,
    activeRoute,
  };
};
